import type { ReactElement } from 'react';
import { describeApiError } from '@/lib/apiErrorView';
import { PermissionDeniedBody } from '@/pages/PermissionDenied';

export interface ApiErrorNoticeProps {
  /** Whatever was caught. */
  error: unknown;
  /** Caller text used when nothing presentable can be derived. */
  fallback: string;
  /** When present a Retry button is offered (also for a 403 — permissions can change). */
  onRetry?: () => void;
  /** What failed, e.g. "Could not load validation rules" — the title in place of the generic one (not on a 403 card). */
  heading?: string;
  /** Label for the retry button (default "Retry") so callers can name what retries. */
  retryLabel?: string;
}

/**
 * T-17 D11 — the one look for an API failure. A 403 reuses the PermissionDenied
 * card; every other kind is a compact alert. Never prints raw JSON. A server
 * error shows the correlation id when the body carries one.
 */
export function ApiErrorNotice({ error, fallback, onRetry, heading, retryLabel }: ApiErrorNoticeProps): ReactElement {
  const view = describeApiError(error, fallback);

  if (view.kind === 'forbidden') {
    return (
      <div role="alert" data-testid="api-error-notice" data-kind={view.kind}>
        <PermissionDeniedBody
          inline
          detail={view.message}
          {...(view.permission !== undefined && { permission: view.permission })}
          {...(onRetry !== undefined && { onRetry })}
        />
      </div>
    );
  }

  return (
    <div
      role="alert"
      data-testid="api-error-notice"
      data-kind={view.kind}
      style={{
        margin: '16px 0',
        padding: 12,
        border: '1px solid #fca5a5',
        borderRadius: 6,
        background: '#fef2f2',
        color: '#991b1b',
        fontSize: 14,
      }}
    >
      <strong>{heading ?? view.title}</strong>
      <div style={{ marginTop: 4 }}>{view.message}</div>
      {view.correlationId && (
        <div data-testid="api-error-correlation" style={{ marginTop: 4, fontSize: 12 }}>
          Reference: <code>{view.correlationId}</code> — quote this when reporting the problem.
        </div>
      )}
      {onRetry && (
        <button
          type="button"
          data-testid="api-error-retry"
          onClick={onRetry}
          style={{ marginTop: 8, padding: '4px 12px', cursor: 'pointer' }}
        >
          {retryLabel ?? 'Retry'}
        </button>
      )}
    </div>
  );
}
